import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useReducer,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Badge,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Input,
  SpinnerLoader,
  Switch,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DataTable,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SingleSelect,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import {
  Search,
  Plus,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Edit,
  X,
} from "lucide-react";

import apiClient from "@lift-admin/utils/apiClient";
import { useAuthContext } from "@lift-admin/utils/provider";
import { Toastify } from 'lift-ui';
import { actionLog } from "@/App";
import { buildSummary } from "@lift-admin/utils/logChangeFormatter";
import CreateDialog from "./components/create-dialog.component";
import { cn } from "@/lib/utils";

const taxonomyTypes = ["campaign", "ad", "adset"];

// Type Definitions

export interface SDType {
  id: number;
  name: string;
  taxonomy_type: string;
  description?: string;
  is_active: boolean;
  created_by: {
    id: number;
    name: string;
    image: string;
  };
  user_info: {
    name: string;
    [key: string]: any;
  };
  values: any;
  display_name: string;
}

export interface SDValue {
  id: number;
  value: string;
  display_name: string;
  domain_type_id: number;
  domain_type: {
    id: number;
    name: string;
  };
  created_by: {
    id: number;
    name: string;
    image: string;
  };
  created_at: Date;
  updated_at: Date;
  is_active: boolean;
  user_info: {
    name: string;
    [key: string]: any;
  };
}

export interface CDType {
  id: number;
  name: string;
  taxonomy_type: string;
  description?: string;
  is_required: boolean;
  created_by: {
    id: number;
    name: string;
    image: string;
  };
  brand_id: number;
  brand: {
    id: number;
    name: string;
    image: string;
  };
  is_active: boolean;
  user_info: {
    name: string;
    [key: string]: any;
  };
  values: any;
  display_name: string;
}

export interface CDValue {
  id: number;
  display_name: string;
  value: string;
  custom_domain_type_id: string;
  domain_type: {
    id: number;
    name: string;
  };
  metadata?: string;
  created_by: {
    id: number;
    name: string;
    image: string;
  };
  brand_id: number;
  is_active: boolean;
  user_info: {
    name: string;
    [key: string]: any;
  };
}

// Error Handler
export const handleApiError = (error: any) => {
  console.error("API Error:", error);
  const errorMessage =
    error.response?.data?.detail ||
    error.message ||
    "An unexpected error occurred";
  Toastify("error", errorMessage);
  return errorMessage;
};

// Reducer for aggregator open rows
const aggregatorReducer = (
  prev: Record<string, boolean>,
  action: { type: "TOGGLE"; key: string }
) => {
  switch (action.type) {
    case "TOGGLE":
      return { ...prev, [action.key]: !prev[action.key] };
    default:
      return prev;
  }
};

// Interface for GroupRow component props
interface GroupRowProps {
  displayName: string;
  groupItems: CDType[];
  aggregatorOpen: boolean;
  toggleGroup: (displayName: string) => void;
  itemOpenRows: Record<number, boolean>;
  toggleItem: (itemId: number) => void;
  setEditValue: React.Dispatch<React.SetStateAction<any>>;
  setDialogMode: React.Dispatch<React.SetStateAction<string>>;
  handleCreateClick: () => void;
  setAboutToStatusChange: React.Dispatch<React.SetStateAction<any>>;
  setCreateModeValue: React.Dispatch<React.SetStateAction<any>>;
  hasEditAccess?: boolean;
}

// Helper: build DataTable columns for expanded values inside Standard tab
function buildStandardValueColumns(
  parentTypeId: number,
  hasEditAccess: boolean,
  setAboutToStatusChange: React.Dispatch<React.SetStateAction<any>>,
  setEditValue: React.Dispatch<React.SetStateAction<any>>,
  setDialogMode: React.Dispatch<React.SetStateAction<string>>,
  handleCreateClick: () => void
): TableColumn<any>[] {
  const cols: TableColumn<any>[] = [
    { key: "id", header: "ID", accessor: (row: any) => row.id, width: "80px" },
    { key: "display_name", header: "Display Name", accessor: (row: any) => row.display_name, width: "25%", render: (_v: any, row: any) => <span className="font-medium">{row.display_name}</span> },
    { key: "value", header: "Value", accessor: (row: any) => row.value, width: "25%" },
  ];
  if (hasEditAccess) {
    cols.push({
      key: "is_active",
      header: "Status",
      accessor: (row: any) => row.is_active,
      width: "100px",
      render: (_v: any, row: any) => (
        <div onClick={(e) => e.stopPropagation()} className="w-fit">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Switch
                    checked={row.is_active}
                    onCheckedChange={() =>
                      setAboutToStatusChange([
                        row.id,
                        !row.is_active,
                        1,
                        row.display_name || row.value || row.name,
                      ])
                    }
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>{row.is_active ? "Click to deactivate" : "Click to activate"}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    });
  }
  cols.push({
    key: "created_by",
    header: "Created By",
    accessor: (row: any) => row.created_by?.name ?? "-",
    width: "20%",
    render: (_v: any, row: any) =>
      row.created_by ? (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 bg-gray-100">
            <img src={row.created_by.image} alt="creator" className="w-full h-full object-cover" />
          </div>
          <span className="text-sm">{row.created_by.name}</span>
        </div>
      ) : (
        "-"
      ),
  });
  if (hasEditAccess) {
    cols.push({
      key: "action",
      header: "Action",
      accessor: () => "",
      width: "80px",
      render: (_v: any, val: any) => (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => {
            setEditValue({
              id: val.id,
              name: val.display_name,
              type: parentTypeId,
              value: val.value,
              description: "",
              is_active: val.is_active,
              active_tab: "Value",
            });
            setDialogMode("edit");
            handleCreateClick();
          }}
        >
          <Edit className="h-4 w-4" />
        </Button>
      ),
    });
  }
  return cols;
}

// Helper: build DataTable columns for expanded values inside Custom tab
function buildCustomValueColumns(
  parentTypeId: number,
  hasEditAccess: boolean,
  setAboutToStatusChange: React.Dispatch<React.SetStateAction<any>>,
  setEditValue: React.Dispatch<React.SetStateAction<any>>,
  setDialogMode: React.Dispatch<React.SetStateAction<string>>,
  handleCreateClick: () => void
): TableColumn<any>[] {
  const cols: TableColumn<any>[] = [
    { key: "id", header: "ID", accessor: (row: any) => row.id, width: "80px" },
    { key: "display_name", header: "Display Name", accessor: (row: any) => row.display_name, width: "25%", render: (_v: any, row: any) => <span className="font-medium">{row.display_name}</span> },
    { key: "value", header: "Value", accessor: (row: any) => row.value, width: "25%" },
  ];
  if (hasEditAccess) {
    cols.push({
      key: "is_active",
      header: "Status",
      accessor: (row: any) => row.is_active,
      width: "100px",
      render: (_v: any, row: any) => (
        <div onClick={(e) => e.stopPropagation()} className="w-fit">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Switch
                    checked={row.is_active}
                    onCheckedChange={() =>
                      setAboutToStatusChange([
                        row.id,
                        !row.is_active,
                        1,
                        row.display_name || row.value || row.name,
                      ])
                    }
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>{row.is_active ? "Click to deactivate" : "Click to activate"}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    });
  }
  cols.push({
    key: "created_by",
    header: "Created By",
    accessor: (row: any) => row.created_by?.name ?? "-",
    width: "20%",
    render: (_v: any, row: any) =>
      row.created_by ? (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 bg-gray-100">
            <img src={row.created_by.image} alt="creator" className="w-full h-full object-cover" />
          </div>
          <span className="text-sm">{row.created_by.name}</span>
        </div>
      ) : (
        "-"
      ),
  });
  if (hasEditAccess) {
    cols.push({
      key: "action",
      header: "Action",
      accessor: () => "",
      width: "80px",
      render: (_v: any, val: any) => (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => {
            setEditValue({
              id: val.id,
              name: val.display_name,
              type: parentTypeId,
              value: val.value,
              description: "",
              is_active: val.is_active,
              active_tab: "Value",
            });
            setDialogMode("edit");
            handleCreateClick();
          }}
        >
          <Edit className="h-4 w-4" />
        </Button>
      ),
    });
  }
  return cols;
}

// GroupRow component for grouped custom types
const GroupRow = React.memo<GroupRowProps>(
  ({
    displayName,
    groupItems,
    aggregatorOpen,
    toggleGroup,
    itemOpenRows,
    toggleItem,
    setEditValue,
    setDialogMode,
    handleCreateClick,
    setAboutToStatusChange,
    setCreateModeValue,
    hasEditAccess = false,
  }) => {
    const taxonomyTypesSet = new Set(groupItems.map((it) => it.taxonomy_type));
    const taxonomyType =
      taxonomyTypesSet.size === 1 ? groupItems[0].taxonomy_type : "Multiple";

    const brandSet = new Set(
      groupItems.map((it) => (it.brand ? it.brand.name : "-"))
    );
    const brandCount = brandSet.size;
    const brands = Array.from(brandSet).slice(0, 3);

    const statusSet = new Set(
      groupItems.map((it) => (it.is_active ? "Active" : "Inactive"))
    );
    const status =
      statusSet.size === 1
        ? groupItems[0].is_active
          ? "Active"
          : "Inactive"
        : "Multiple";

    const firstThreeCreators = groupItems
      .slice(0, 3)
      .map((item) => item.created_by);

    return (
      <>
        {/* Group Header Row */}
        <TableRow
          className={cn(
            "hover:bg-muted/50 cursor-pointer",
            aggregatorOpen && "bg-muted/50 sticky top-14 z-[2]"
          )}
          onClick={() => toggleGroup(displayName)}
        >
          <TableCell className="w-12">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              {aggregatorOpen ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </Button>
          </TableCell>
          <TableCell></TableCell>
          <TableCell className="font-medium">{displayName}</TableCell>
          <TableCell></TableCell>
          <TableCell>{taxonomyType}</TableCell>
          <TableCell>
            {brands.join(", ")}
            {brandCount > 3 && ` +${brandCount - 3}`}
          </TableCell>
          {hasEditAccess && (
          <TableCell>
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "w-2.5 h-2.5 rounded-full",
                    status === "Active"
                    ? "bg-green-500"
                      : status === "Inactive"
                      ? "bg-gray-400"
                      : "bg-orange-500"
                )}
              />
              {status}
            </div>
          </TableCell>
          )}
          <TableCell>
            <div className="flex items-center">
              {firstThreeCreators.map((creator, index) =>
                creator && creator.image ? (
                  <img
                    key={index}
                    src={creator.image}
                    alt={`creator ${index + 1}`}
                    className={cn(
                      "w-8 h-8 rounded-full object-cover border-2 border-white",
                      index > 0 && "-ml-2"
                    )}
                  />
                ) : null
              )}
              {groupItems.length > 3 && (
                <span className="text-xs text-muted-foreground ml-1">
                  +{groupItems.length - 3} more
                </span>
              )}
            </div>
          </TableCell>
          {hasEditAccess && <TableCell />}
        </TableRow>

        {/* Expanded child rows */}
        {aggregatorOpen && (
          <>
            {groupItems.map((customType) => (
              <React.Fragment key={customType.id}>
                <TableRow className="bg-muted/30 hover:bg-muted/50">
                  <TableCell className="w-12">
                    {customType.values?.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleItem(customType.id);
                        }}
                      >
                        {itemOpenRows[customType.id] ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>{customType.id}</TableCell>
                  <TableCell>{customType.name}</TableCell>
                  <TableCell>{customType.display_name}</TableCell>
                  <TableCell>{customType.taxonomy_type}</TableCell>
                  <TableCell>
                    {customType.brand ? customType.brand.name : "-"}
                  </TableCell>
                  {hasEditAccess && (
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <Switch
                        checked={customType.is_active}
                              onCheckedChange={() =>
                          setAboutToStatusChange([
                            customType.id,
                            !customType.is_active,
                            0,
                            customType.name,
                          ])
                        }
                      />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          {customType.is_active
                            ? "Click to deactivate"
                            : "Click to activate"}
                        </TooltipContent>
                    </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  )}
                  <TableCell>
                    {customType.created_by ? (
                      <div className="flex items-center gap-2">
                        <img
                          src={customType.created_by.image}
                          alt="creator"
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <span className="text-sm">{customType.created_by.name}</span>
                      </div>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  {hasEditAccess && (
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                        setEditValue({
                          ...customType,
                          type: customType.taxonomy_type,
                          active_tab: "Type",
                          brand_id: customType.brand?.id,
                          is_mandatory: customType.is_required,
                        });
                        setDialogMode("edit");
                        handleCreateClick();
                      }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                        setCreateModeValue({
                          active_tab: "Value",
                          type_id: customType.id,
                        });
                        handleCreateClick();
                      }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  )}
                </TableRow>

                {/* Values nested table */}
                {itemOpenRows[customType.id] && customType.values?.length > 0 && (
                    <TableRow>
                    <TableCell colSpan={hasEditAccess ? 9 : 7} className="p-0">
                      <div className="m-4">
                        <p className="text-sm font-medium mb-2">
                          Values for {customType.name}
                        </p>
                        <div className="[&_.overflow-auto]:!min-h-0">
                          <DataTable<any>
                            columns={buildCustomValueColumns(customType.id, hasEditAccess, setAboutToStatusChange, setEditValue, setDialogMode, handleCreateClick)}
                            data={customType.values}
                            loading={false}
                            emptyMessage="No values"
                            showActions={false}
                            stickyHeader
                          />
                        </div>
                                            </div>
                      </TableCell>
                    </TableRow>
                  )}
              </React.Fragment>
            ))}
          </>
        )}
      </>
    );
  }
);

// Main Component
const TaxonomyName = () => {
  // Permission checks for taxonomy management
  const { canEdit, canManage } = useAuthContext();
  const hasEditAccess = canEdit('taxonomy');
  const hasManageAccess = canManage('taxonomy');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [activeTab, setActiveTab] = useState(0);
  const [mainTab, setMainTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isSearching, setIsSearching] = useState(false);
  const [isCalculatingGroups, setIsCalculatingGroups] = useState(false);

  const [standardTypes, setStandardTypes] = useState<SDType[]>([]);
  const [standardValues, setStandardValues] = useState<SDValue[]>([]);
  const [customTypes, setCustomTypes] = useState<CDType[]>([]);
  const [customValues, setCustomValues] = useState<CDValue[]>([]);
  const [dialogType, setDialogType] = useState<any[]>([]);
  const [dialogValue, setDialogValue] = useState<any[]>([]);
  const [editValue, setEditValue] = useState<any>({});
  const [dialogMode, setDialogMode] = useState<string>("add");
  const [refresh, setRefresh] = useState(false);
  const [brands, setBrands] = useState<any[]>([]);
  const [globalTaxonomyType, setGlobalTaxonomyType] = useState("");
  const [backupValues, setBackupValues] = useState<any>([]);

  const [openTypeCreateDialog, setOpenTypeCreateDialog] = useState(false);
  const [createModeValue, setCreateModeValue] = useState<any>({});
  const [aboutToStatusChange, setAboutToStatusChange] = useState<any>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [openRows, setOpenRows] = useState<Record<number, boolean>>({});
  const [itemOpenRows, setItemOpenRows] = useState<Record<number, boolean>>({});
  const [standardOpenRows, setStandardOpenRows] = useState<Record<number, boolean>>({});
  const [valuesSheetType, setValuesSheetType] = useState<SDType | null>(null);

  const [aggregatorOpenRows, dispatchAggregator] = useReducer(
    aggregatorReducer,
    {}
  );

  const toggleGroup = useCallback((displayName: string) => {
    dispatchAggregator({ type: "TOGGLE", key: displayName });
  }, []);

  const toggleItem = useCallback((itemId: number) => {
    setItemOpenRows((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  }, []);

  const toggleStandardRow = useCallback((typeId: number) => {
    setStandardOpenRows((prev) => ({
      ...prev,
      [typeId]: !prev[typeId],
    }));
  }, []);

  const toggleRow = useCallback((id: number) => {
    setOpenRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const handleCreateClick = useCallback(
    () => setOpenTypeCreateDialog(true),
    []
  );

  const handleChangePage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((value: number) => {
    setRowsPerPage(value);
      setPage(0);
  }, []);

  const handleMainTabChange = useCallback((value: string) => {
    setMainTab(value === "standard" ? 0 : 1);
      setActiveTab(0);
      setPage(0);
  }, []);

  const handleGlobalTaxonomyTypeChange = useCallback((type: string) => {
    setGlobalTaxonomyType(type);
    setPage(0);
  }, []);

  // Data Fetching
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let standardResponse;
      let customResponse;

      const endpoint = `txnmy/api/v1/${activeTab === 0 ? "sdtype/" : "sdvalue/"}${
        globalTaxonomyType ? `?tax_type=${globalTaxonomyType}` : ""
        }`;

      const customEndpoint = `txnmy/api/v1/${activeTab === 0 ? "cdtype/" : "cdvalue/"}${
        globalTaxonomyType ? `?tax_type=${globalTaxonomyType}` : ""
        }`;

      if (mainTab === 0) {
        standardResponse = await apiClient.get(endpoint);
      } else {
        customResponse = await apiClient.get(customEndpoint);
      }

      if (standardResponse && standardResponse.data.message === "success") {
        const types = standardResponse.data.response;

        if (activeTab === 0) {
          setStandardTypes(types.sdtypes);
          setDialogType(taxonomyTypes);
          if (mainTab === 0) setBackupValues(types.sdtypes);
          setDialogValue(types.sdtype_minimal);
        } else {
          setStandardValues(types.sdvalues);
          setDialogType(types.sdtypes);
          if (mainTab === 0) setBackupValues(types.sdvalues);
        }
      }

      if (customResponse && customResponse.data.message === "success") {
        const res = customResponse.data.response;

        if (activeTab === 0) {
          setCustomTypes(res.cdtypes);
          if (mainTab === 1) {
            setDialogType(taxonomyTypes);
            setBackupValues(res.cdtypes);
            setDialogValue(res.cd_types_minimal);
          }
          setBrands(res.brands);
        } else {
          setCustomValues(res.cdvalues);
          if (mainTab === 1) {
            setDialogType(res.cdtypes);
            setBackupValues(res.cdvalues);
          }
        }
      }
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, mainTab, globalTaxonomyType]);

  // Status Update
  const handleStatusUpdate = async (
    id: number,
    status: boolean,
    active_tab: number,
    entityName?: string
  ) => {
    setIsLoading(true);
    const path: string =
      mainTab === 0
        ? active_tab === 0
          ? `sdtype/update/${id}`
          : `sdvalue/update/${id}`
        : active_tab === 0
          ? `cdtype/update/${id}`
          : `cdvalue/update/${id}`;
    const entityType = `${mainTab === 0 ? "Standard" : "Custom"} ${
      active_tab === 0 ? "Type" : "Value"
    }`;
    const nameForLog = entityName || `ID ${id}`;
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const response = await apiClient.put(
        `txnmy/api/v1/${path}`,
        {
          is_active: status,
          updated_by: user.id,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.data.message === "success") {
        const changes = [
          {
            label: "Status",
            from: status ? "Inactive" : "Active",
            to: status ? "Active" : "Inactive",
          },
        ];
        const logDesc = {
          message: buildSummary(
            entityType,
            nameForLog,
            status ? "activated" : "deactivated"
          ),
          status: response.status,
          method: response.config?.method || "PUT",
          url: response.config?.url || `/txnmy/api/v1/${path}`,
          category: "taxonomy",
          action: status
            ? "activate_taxonomy_entity"
            : "deactivate_taxonomy_entity",
          entity_id: id,
          entity_name: nameForLog,
          changes,
        };
        await actionLog?.actionLogTrack(JSON.stringify(logDesc), false);

        Toastify(
          "success",
          `${entityType} status updated successfully.`
        );
        setRefresh((prev: boolean) => !prev);
        setAboutToStatusChange(null);
      } else {
        setAboutToStatusChange(null);
        throw new Error("Unexpected response format.");
      }
    } catch (error: any) {
      console.error(error);
      Toastify(
        "error",
        error.response?.data?.detail || `Failed to update status`
      );

      const errorLogDesc = {
        message: `Failed to update status for ${entityType} "${nameForLog}": ${error.response?.data?.detail || "Failed to update status"}`,
        status: error.response?.status || 500,
        method: "PUT",
        url: `/txnmy/api/v1/${path}`,
        category: "taxonomy",
        action: status
          ? "activate_taxonomy_entity"
          : "deactivate_taxonomy_entity",
        entity_id: id,
        entity_name: nameForLog,
        error: error.response?.data?.detail || "Failed to update status",
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorLogDesc), true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData, activeTab, refresh, globalTaxonomyType]);

  // Keep the values sheet in sync after data refreshes (e.g. status toggle) - used by custom tab
  useEffect(() => {
    if (!valuesSheetType) return;
    const source = mainTab === 0 ? standardTypes : customTypes;
    const updated = source.find((t: any) => t.id === valuesSheetType.id);
    if (updated && updated !== valuesSheetType) {
      setValuesSheetType(updated as SDType);
    }
  }, [standardTypes, customTypes, mainTab, valuesSheetType]);

  // Search function
  const searchResults = (query: string) => {
    setIsSearching(true);
    const queryLower = query.trim().toLowerCase();
    let results: any[] = [];

    if (queryLower === "") {
      if (mainTab === 0) {
        activeTab === 0
          ? setStandardTypes([...backupValues])
          : setStandardValues([...backupValues]);
      } else {
        activeTab === 0
          ? setCustomTypes([...backupValues])
          : setCustomValues([...backupValues]);
      }
      setIsSearching(false);
      return;
    }

    for (let i = 0; i < backupValues.length; i++) {
      if (mainTab === 0) {
        if (activeTab === 0) {
          const type = backupValues[i];
          const typeMatches =
            type.name?.toLowerCase().includes(queryLower) ||
            type.taxonomy_type?.toLowerCase().includes(queryLower) ||
            type.display_name?.toLowerCase().includes(queryLower) ||
            type.created_by?.name?.toLowerCase().includes(queryLower);

          const hasMatchingValues = type.values?.some(
            (value: any) =>
              value.display_name?.toLowerCase().includes(queryLower) ||
              value.value?.toLowerCase().includes(queryLower) ||
              value.created_by?.name?.toLowerCase().includes(queryLower)
          );

          if (typeMatches || hasMatchingValues) {
            results.push({
              ...type,
              values: type.values,
              matchedSearch: true,
              valuesMatched: hasMatchingValues && !typeMatches,
            });
          }
        } else if (activeTab === 1) {
          const value = backupValues[i];
          if (
            value.display_name?.toLowerCase().includes(queryLower) ||
            value.value?.toLowerCase().includes(queryLower) ||
            value.domain_type?.name?.toLowerCase().includes(queryLower) ||
            value.created_by?.name?.toLowerCase().includes(queryLower)
          ) {
            results.push(value);
          }
        }
      } else {
        if (activeTab === 0) {
          const type = backupValues[i];
          const typeMatches =
            type.name?.toLowerCase().includes(queryLower) ||
            type.taxonomy_type?.toLowerCase().includes(queryLower) ||
            type.brand?.name?.toLowerCase().includes(queryLower) ||
            type.display_name?.toLowerCase().includes(queryLower) ||
            type.created_by?.name?.toLowerCase().includes(queryLower);

          const hasMatchingValues = type.values?.some(
            (value: any) =>
              value.display_name?.toLowerCase().includes(queryLower) ||
              value.value?.toLowerCase().includes(queryLower) ||
              value.created_by?.name?.toLowerCase().includes(queryLower)
          );

          if (typeMatches || hasMatchingValues) {
            results.push({
              ...type,
              values: type.values,
              matchedSearch: true,
              valuesMatched: hasMatchingValues && !typeMatches,
            });
          }
        } else if (activeTab === 1) {
          const value = backupValues[i];
          if (
            value.display_name?.toLowerCase().includes(queryLower) ||
            value.value?.toLowerCase().includes(queryLower) ||
            value.domain_type?.name?.toLowerCase().includes(queryLower) ||
            value.created_by?.name?.toLowerCase().includes(queryLower)
          ) {
            results.push(value);
          }
        }
      }
    }

    const newOpenRows = { ...openRows };
    results.forEach((item) => {
      if (item.valuesMatched) {
        newOpenRows[item.id] = true;
      }
    });
    setOpenRows(newOpenRows);

    if (mainTab === 0) {
      activeTab === 0 ? setStandardTypes(results) : setStandardValues(results);
    } else {
      activeTab === 0 ? setCustomTypes(results) : setCustomValues(results);
    }

    setIsSearching(false);
  };

  const handleSearchQuery = useCallback(
    (query: string) => {
      searchResults(query);
    },
    [backupValues, mainTab, activeTab, openRows]
  );

  // Re-apply search filter after data refreshes so the search text persists
  useEffect(() => {
    if (searchText && backupValues.length > 0) {
      searchResults(searchText);
    }
  }, [backupValues]);

  const combinedRows = useMemo(() => {
    const groupsMap: Record<string, CDType[]> = {};
    customTypes.forEach((item) => {
      const key = item.name || "Unnamed";
      if (!groupsMap[key]) groupsMap[key] = [];
      groupsMap[key].push(item);
    });

    const rows: Array<
      | { type: "group"; displayName: string; items: CDType[] }
      | { type: "single"; item: CDType }
    > = [];
    Object.entries(groupsMap).forEach(([key, items]) => {
      if (items.length > 10) {
        rows.push({ type: "group", displayName: key, items });
      } else {
        items.forEach((item) => rows.push({ type: "single", item }));
      }
    });
    return rows;
  }, [customTypes]);

  useEffect(() => {
    if (mainTab === 1 && activeTab === 0 && customTypes.length > 0) {
      setIsCalculatingGroups(true);
      setTimeout(() => setIsCalculatingGroups(false), 0);
    }
  }, [customTypes, mainTab, activeTab]);

  const isAnyLoading = useMemo(
    () => isLoading || isSearching || isCalculatingGroups,
    [isLoading, isSearching, isCalculatingGroups]
  );


  // Get current data for display
  const currentData = mainTab === 0 ? standardTypes : customTypes;
  const currentValues = mainTab === 0 ? standardValues : customValues;
  const totalCount = mainTab === 0 ? standardTypes.length : combinedRows.length;

  // Paginated data for standard tab
  const paginatedStandardData = useMemo(() => {
    return standardTypes.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [standardTypes, page, rowsPerPage]);

  // Paginated data for custom tab
  const paginatedCustomData = useMemo(() => {
    return combinedRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [combinedRows, page, rowsPerPage]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title="Naming Taxonomy"
        renderCustomTop={
          <div className="flex items-center gap-4 flex-wrap">
            {activeTab === 0 && (
              <>
                <SingleSelect
                  options={[
                    { label: "All Types", value: "all" },
                    ...taxonomyTypes.map((type) => ({
                      label: type.charAt(0).toUpperCase() + type.slice(1),
                      value: type,
                    })),
                  ]}
                  defaultValue={globalTaxonomyType || "all"}
                  onChange={(value) =>
                    handleGlobalTaxonomyTypeChange(value === "all" ? "" : value)
                  }
                  placeholder="Taxonomy Type"
                  containerClassName="w-[180px]"
                />
                {globalTaxonomyType && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleGlobalTaxonomyTypeChange("")}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData()}
              disabled={isAnyLoading}
              className="gap-1 px-3 h-8 text-sm"
            >
              <RefreshCw className={`h-4 w-4 ${isAnyLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            {hasEditAccess && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        handleCreateClick();
                        setCreateModeValue({ active_tab: "Type" });
                      }}
                      className="gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Create New
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Create new {activeTab === 0 ? "type" : "value"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  handleSearchQuery(e.target.value);
                }}
                className="pl-10 w-64"
              />
            </div>
          </div>
        }
      />

        {/* Status Change Dialog */}
        <Dialog
        open={aboutToStatusChange !== null}
        onOpenChange={() => setAboutToStatusChange(null)}
      >
          <DialogContent>
          <DialogHeader>
            <DialogTitle>Status Change</DialogTitle>
            <DialogDescription>
              Are you sure you want to change the status?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAboutToStatusChange(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleStatusUpdate(
                  aboutToStatusChange[0],
                  aboutToStatusChange[1],
                  aboutToStatusChange[2],
                  aboutToStatusChange[3]
                );
              }}
              disabled={isLoading}
            >
              {isLoading ? <SpinnerLoader className="h-4 w-4" /> : "Yes"}
            </Button>
          </DialogFooter>
        </DialogContent>
        </Dialog>

      <div className="flex-1 overflow-hidden flex flex-col min-h-0 px-8 pt-4 pb-4">
        {/* Main Tabs */}
            <Tabs
          value={mainTab === 0 ? "standard" : "custom"}
          onValueChange={handleMainTabChange}
          className="w-full flex-1 flex flex-col min-h-0"
        >
          <TabsList className="inline-flex w-fit mb-4">
            <TabsTrigger value="standard" className="px-6 font-semibold" disabled={isAnyLoading}>
                    STANDARD
            </TabsTrigger>
            <TabsTrigger value="custom" className="px-6 font-semibold" disabled={isAnyLoading}>
                    CUSTOM
            </TabsTrigger>
          </TabsList>

          {/* Standard Tab Content */}
          <TabsContent value="standard" className="mt-0 flex-1 overflow-hidden flex flex-col min-h-0">
            <div className="w-full flex flex-col flex-1 min-h-0">
              <div className="overflow-auto relative flex-1" style={{ maxHeight: "calc(100vh - 200px)" }}>
                {isAnyLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <SpinnerLoader className="h-8 w-8" />
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center py-20 text-destructive">
                    {error}
                  </div>
                ) : (
                  <Table>
                    <TableHeader className="sticky top-[-1px] bg-background z-10">
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead className="font-semibold">Id</TableHead>
                        <TableHead className="font-semibold">Display Name</TableHead>
                        <TableHead className="font-semibold">BQ Column Name</TableHead>
                        <TableHead className="font-semibold">Taxonomy Type</TableHead>
                        {hasEditAccess && <TableHead className="font-semibold">Status</TableHead>}
                        <TableHead className="font-semibold">Created By</TableHead>
                        {hasEditAccess && <TableHead className="font-semibold">Action</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedStandardData.length > 0 ? (
                        paginatedStandardData.map((row) => (
                          <React.Fragment key={row.id}>
                            <TableRow className="hover:bg-muted/50">
                              <TableCell className="w-12">
                                {row.values?.length > 0 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => toggleStandardRow(row.id)}
                                  >
                                    {standardOpenRows[row.id] ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell>{row.id}</TableCell>
                              <TableCell><span className="font-medium">{row.name}</span></TableCell>
                              <TableCell>{row.display_name}</TableCell>
                              <TableCell>{row.taxonomy_type}</TableCell>
                              {hasEditAccess && (
                                <TableCell>
                                  <div onClick={(e) => e.stopPropagation()} className="w-fit">
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="inline-flex">
                                            <Switch
                                              checked={row.is_active}
                                              onCheckedChange={() =>
                                              setAboutToStatusChange([
                                                row.id,
                                                !row.is_active,
                                                0,
                                                row.name || row.display_name,
                                              ])
                                            }
                                            />
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent>{row.is_active ? "Click to deactivate" : "Click to activate"}</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                </TableCell>
                              )}
                              <TableCell>
                                {row.created_by ? (
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-gray-100">
                                      <img src={row.created_by.image} alt="creator" className="w-full h-full object-cover" />
                                    </div>
                                    <span className="text-sm">{row.created_by.name}</span>
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              {hasEditAccess && (
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() => {
                                        setEditValue({ ...row, type: row.taxonomy_type, active_tab: "Type" });
                                        setDialogMode("edit");
                                        handleCreateClick();
                                      }}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() => {
                                        setCreateModeValue({ active_tab: "Value", type_id: row.id });
                                        handleCreateClick();
                                      }}
                                    >
                                      <Plus className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>

                            {standardOpenRows[row.id] && row.values?.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={hasEditAccess ? 8 : 6} className="p-0">
                                  <div className="m-4">
                                    <p className="text-sm font-medium mb-2">
                                      Values for {row.name}
                                    </p>
                                    <div className="[&_.overflow-auto]:!min-h-0">
                                      <DataTable<any>
                                        columns={buildStandardValueColumns(row.id, hasEditAccess, setAboutToStatusChange, setEditValue, setDialogMode, handleCreateClick)}
                                        data={row.values}
                                        loading={false}
                                        emptyMessage="No values"
                                        showActions={false}
                                        stickyHeader
                                      />
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={hasEditAccess ? 8 : 6} className="h-24 text-center text-muted-foreground">
                            No data found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>
              <div className="w-full">
                {/* Pagination */}
                <div className="flex items-center justify-between px-2 py-4 bg-background border-t w-full">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-muted-foreground">Items per page</span>
                      <div className="relative">
                        <select
                          value={rowsPerPage}
                          onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                          className="h-8 w-16 rounded border border-input bg-background px-2 pr-6 text-sm focus:outline-none focus:ring-2 focus:ring-ring appearance-none [&::-webkit-appearance]:none"
                        >
                          {[50, 100, 200].map((size) => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-1 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPage(page - 1)}
                        disabled={page === 0}
                        className="h-8 w-8 p-0 hover:bg-muted disabled:opacity-50 bg-transparent"
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        disabled={(page + 1) * rowsPerPage >= standardTypes.length}
                        className="h-8 w-8 p-0 hover:bg-muted disabled:opacity-50 bg-transparent"
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>

                    <span className="text-sm text-muted-foreground">
                      {standardTypes.length > 0 ? page * rowsPerPage + 1 : 0} - {Math.min((page + 1) * rowsPerPage, standardTypes.length)} of {standardTypes.length.toLocaleString()}
                    </span>
                  </div>
                  <div></div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Custom Tab Content */}
          <TabsContent value="custom" className="mt-0 flex-1 flex flex-col min-h-0">
            <div className="w-full flex flex-col flex-1 min-h-0">
              <div className="overflow-auto relative flex-1" style={{ maxHeight: "calc(100vh - 200px)" }}>
                {isAnyLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <SpinnerLoader className="h-8 w-8" />
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center py-20 text-destructive">
                    {error}
                  </div>
                ) : (
                  <Table>
                    <TableHeader className="sticky top-[-1px] bg-background z-10">
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead className="font-semibold">Id</TableHead>
                        <TableHead className="font-semibold">Display Name</TableHead>
                        <TableHead className="font-semibold">BQ Column Name</TableHead>
                        <TableHead className="font-semibold">Taxonomy Type</TableHead>
                        <TableHead className="font-semibold">Brand</TableHead>
                        {hasEditAccess && <TableHead className="font-semibold">Status</TableHead>}
                        <TableHead className="font-semibold">Created By</TableHead>
                        {hasEditAccess && <TableHead className="font-semibold">Action</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedCustomData.length > 0 ? (
                        paginatedCustomData.map((row, idx) => {
                                if (row.type === "group") {
                                  return (
                                    <GroupRow
                                key={`group-${row.displayName}`}
                                      displayName={row.displayName}
                                      groupItems={row.items}
                                aggregatorOpen={!!aggregatorOpenRows[row.displayName]}
                                      toggleGroup={toggleGroup}
                                      itemOpenRows={itemOpenRows}
                                      toggleItem={toggleItem}
                                      setEditValue={setEditValue}
                                      setDialogMode={setDialogMode}
                                      handleCreateClick={handleCreateClick}
                                setAboutToStatusChange={setAboutToStatusChange}
                                      setCreateModeValue={setCreateModeValue}
                                      hasEditAccess={hasEditAccess}
                                    />
                                  );
                                } else {
                                  const item = row.item;
                                  return (
                                    <React.Fragment key={item.id}>
                                <TableRow className="hover:bg-muted/50">
                                  <TableCell className="w-12">
                                          {item.values?.length > 0 && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => toggleRow(item.id)}
                                            >
                                              {openRows[item.id] ? (
                                          <ChevronDown className="h-4 w-4" />
                                              ) : (
                                          <ChevronRight className="h-4 w-4" />
                                              )}
                                      </Button>
                                          )}
                                        </TableCell>
                                        <TableCell>{item.id}</TableCell>
                                  <TableCell className="font-medium">{item.name}</TableCell>
                                  <TableCell>{item.display_name}</TableCell>
                                  <TableCell>{item.taxonomy_type}</TableCell>
                                  <TableCell>{item.brand ? item.brand.name : "-"}</TableCell>
                                        {hasEditAccess && (
                                        <TableCell>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div>
                                            <Switch
                                              checked={item.is_active}
                                              onCheckedChange={() =>
                                                setAboutToStatusChange([
                                                  item.id,
                                                  !item.is_active,
                                                  0,
                                                  item.name,
                                                ])
                                              }
                                            />
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          {item.is_active
                                            ? "Click to deactivate"
                                            : "Click to activate"}
                                        </TooltipContent>
                                          </Tooltip>
                                    </TooltipProvider>
                                        </TableCell>
                                        )}
                                        <TableCell>
                                          {item.created_by ? (
                                      <div className="flex items-center gap-2">
                                              <img
                                                src={item.created_by.image}
                                                alt="creator"
                                          className="w-8 h-8 rounded-full object-cover"
                                              />
                                        <span className="text-sm">{item.created_by.name}</span>
                                            </div>
                                          ) : (
                                            "-"
                                          )}
                                        </TableCell>
                                  {hasEditAccess && (
                                  <TableCell>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                            onClick={() => {
                                              setEditValue({
                                            ...item,
                                                type: item.taxonomy_type,
                                                active_tab: "Type",
                                            brand_id: item.brand?.id,
                                            is_mandatory: item.is_required,
                                              });
                                              setDialogMode("edit");
                                              handleCreateClick();
                                            }}
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                            onClick={() => {
                                              setCreateModeValue({
                                                active_tab: "Value",
                                                type_id: item.id,
                                              });
                                              handleCreateClick();
                                            }}
                                      >
                                        <Plus className="h-4 w-4" />
                                      </Button>
                                    </div>
                                        </TableCell>
                                  )}
                                      </TableRow>

                                {/* Expanded values */}
                                {openRows[item.id] && item.values?.length > 0 && (
                                  <TableRow>
                                    <TableCell colSpan={hasEditAccess ? 9 : 7} className="p-0">
                                      <div className="m-4">
                                        <p className="text-sm font-medium mb-2">
                                                  Values for {item.name}
                                        </p>
                                        <div className="[&_.overflow-auto]:!min-h-0">
                                          <DataTable<any>
                                            columns={buildCustomValueColumns(item.id, hasEditAccess, setAboutToStatusChange, setEditValue, setDialogMode, handleCreateClick)}
                                            data={item.values}
                                            loading={false}
                                            emptyMessage="No values"
                                            showActions={false}
                                            stickyHeader
                                          />
                                        </div>
                                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </React.Fragment>
                                  );
                                }
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={hasEditAccess ? 9 : 7} className="h-24 text-center text-muted-foreground">
                            No data found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>
              <div className="w-full">
                {/* Pagination */}
                <div className="flex items-center justify-between px-2 py-4 bg-background border-t w-full">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-muted-foreground">Items per page</span>
                      <div className="relative">
                        <select
                          value={rowsPerPage}
                          onChange={(e) => handleChangeRowsPerPage(Number(e.target.value))}
                          className="h-8 w-16 rounded border border-input bg-background px-2 pr-6 text-sm focus:outline-none focus:ring-2 focus:ring-ring appearance-none [&::-webkit-appearance]:none"
                        >
                          {[50, 100, 200].map((size) => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-1 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleChangePage(page - 1)}
                        disabled={page === 0}
                        className="h-8 w-8 p-0 hover:bg-muted disabled:opacity-50 bg-transparent"
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleChangePage(page + 1)}
                        disabled={(page + 1) * rowsPerPage >= combinedRows.length}
                        className="h-8 w-8 p-0 hover:bg-muted disabled:opacity-50 bg-transparent"
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>

                    <span className="text-sm text-muted-foreground">
                      {combinedRows.length > 0 ? page * rowsPerPage + 1 : 0} - {Math.min((page + 1) * rowsPerPage, combinedRows.length)} of {combinedRows.length.toLocaleString()}
                    </span>
                  </div>
                  <div></div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

        {/* Dialog Components */}
        <CreateDialog
          openCreateDialog={openTypeCreateDialog}
          setOpenCreateDialog={setOpenTypeCreateDialog}
          mainTab={mainTab === 0 ? "Standard" : "Custom"}
          taxonomyTypes={dialogType}
          dialogValue={dialogValue}
          dialogMode={dialogMode}
          setDialogMode={setDialogMode}
          editValue={editValue}
          setEditValue={setEditValue}
          setRefresh={setRefresh}
          brands={brands}
          createModeValue={createModeValue}
        />
    </div>
  );
};

export default TaxonomyName;
